/**
 * PreviewDocumenActionButtons
 * - This is currently specific to form documents - This will need to be updated if ever used for other documents
 */
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import * as fileDownloadActions from '@payaca/store/fileDownload/fileDownloadActions';

import { SendSimpleCustomEmailControl } from '@/ui/components/sendSimpleCustomEmailControl/SendSimpleCustomEmailControl';
import { Button, Modal } from '@payaca/ambrosia';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import * as documentActions from '@payaca/store/documents/documentActions';
import UntitledIcon from '@payaca/untitled-icons';

import { DocumentActionType } from '@payaca/types/documentActionsTypes';
import { FileDownloadTypes } from '@payaca/types/fileDownloadTypes';

import { getAvailableActionsForDocument } from '@payaca/helpers/documentActionsHelper';

import { getCustomerByDealId, getDocument } from '@/utils/stateAccessors';
import { DocumentsPermissions } from '@payaca/permissions/documents/documents.permissions';
import { downloadPdfData } from '@payaca/utilities/fileUtilities';

import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import './PreviewDocumentActionButtons.sass';

type Props = {
  documentId: number;
  taskId: number | null;
};

const PreviewDocumentActionButtons: FunctionComponent<Props> = ({
  documentId,
  taskId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [showEditDocumentWarning, setShowEditDocumentWarning] = useState(false);
  const [isErrorEditing, setIsErrorEditing] = useState(false);

  const document = useSelector((state) => {
    return getDocument(state, documentId);
  });

  const customer = useSelector((state) => {
    return document?.dealId
      ? getCustomerByDealId(state, document.dealId)
      : undefined;
  });

  const { userHasRequiredPermission } = usePermissions();

  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const isSendingDocument = useSelector(
    (state) => state.documents.isSendingDocument
  );

  const isEditingDocument = useSelector(
    (state) => state.documents.isEditingDocument
  );

  const formId = useMemo(() => document?.formInstances?.[0]?.id, [document]);

  const conditionalText =
    document?.title === 'Job Report'
      ? ''
      : `\n\nThere is an expiry date listed on your certificate. We will get in touch nearer the time to book in your renewal.`;

  const triggerFileDownload = useCallback(
    (fileContent: string) => {
      downloadPdfData(fileContent, document?.title || 'Document');
      setIsDownloadingPdf(false);
    },
    [document]
  );

  const canSendDocument = userHasRequiredPermission([
    DocumentsPermissions.SEND_DOCUMENT,
  ]);
  const availableActions: DocumentActionType[] = useMemo(
    () => getAvailableActionsForDocument(canSendDocument, document),
    [canSendDocument, document]
  );

  const downloadAsPdfIconButton = useMemo(() => {
    if (!document) return;
    return (
      <div className="icon-button-container flex flex-col items-center gap-1">
        <Button
          variant="secondary"
          isProcessing={isDownloadingPdf}
          replaceContentWhenProcessing
          onClick={() => {
            setIsDownloadingPdf(true);
            dispatch(
              fileDownloadActions.requestDownloadFile(
                documentId,
                FileDownloadTypes.DOCUMENT,
                triggerFileDownload
              )
            );
          }}
        >
          <UntitledIcon name="download-01.3" className="h-5 w-5" />
        </Button>
        <span className="text-blue-500 font-normal">Download as pdf</span>
      </div>
    );
  }, [dispatch, documentId, document, triggerFileDownload, isDownloadingPdf]);

  const handleSend = useCallback(
    ({
      sendToEmail,
      emailBody,
      sendACopy,
    }: {
      sendToEmail: string;
      emailBody: string;
      sendACopy: boolean;
    }) => {
      dispatch(
        documentActions.requestSendDocument(
          documentId,
          sendToEmail,
          emailBody,
          sendACopy,
          () => setIsSent(true)
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [documentId]
  );

  const editDocument = useCallback(() => {
    dispatch(
      documentActions.requestEditDocument(documentId, (error, response) => {
        if (!error) {
          history.push(
            `/forms/${
              response?.formInstances?.[0].previewToken
            }/menu?dealId=${document?.dealId}&taskId=${taskId || ''}`
          );
        } else {
          setIsErrorEditing(true);
        }
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'history'
  }, [document?.dealId, documentId, taskId]);

  const onCloseEditDocumentWarningModal = useCallback(() => {
    setIsErrorEditing(false);
    setShowEditDocumentWarning(false);
  }, []);

  if (!document) return null;

  if (isProcessing)
    return (
      <div className="preview-job-action-buttons loader-container">
        <MiniLoader />
      </div>
    );

  return (
    <>
      <div className="preview-job-action-buttons">
        <div className="preview-job-action-buttons-inner">
          {availableActions.includes(DocumentActionType.SEND_A_COPY) &&
            formId && (
              <div className="icon-button-container flex flex-col items-center gap-1">
                <Button
                  variant="secondary"
                  onClick={() => setShowSendModal(true)}
                >
                  <UntitledIcon
                    name="message-text-square-01.3"
                    className="h-5 w-5"
                  />
                </Button>
                <span className="text-blue-500 font-normal">Send a copy</span>
              </div>
            )}
          {availableActions.includes(DocumentActionType.PRINT) &&
            downloadAsPdfIconButton}
          {availableActions.includes(DocumentActionType.EDIT) && formId && (
            <div className="icon-button-container flex flex-col items-center gap-1">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditDocumentWarning(true);
                }}
              >
                <UntitledIcon name="edit-02.3" className="h-5 w-5" />
              </Button>
              <span className="text-blue-500 font-normal">Edit</span>
            </div>
          )}
        </div>
      </div>
      {/* Send document modal */}
      <Modal
        isOpen={showSendModal}
        onClose={() => {
          setShowSendModal(false);
          setIsSent(false);
        }}
        title="Send a copy"
      >
        <Modal.Body>
          {isSent ? (
            <p>A PDF copy of the document has been successfully sent.</p>
          ) : (
            <SendSimpleCustomEmailControl
              customer={customer}
              allowCustomEmailTemplate={true}
              initialSubject={`${account.companyName} has sent you a form`}
              initialEmailBody={`\n\nYour ${document?.title} is complete!${conditionalText}\n\nPlease find your pdf document attached.\n\nThanks,\n\n${account.companyName}`.trim()}
              onSend={handleSend}
              isSending={isSendingDocument}
            />
          )}
        </Modal.Body>
      </Modal>
      {/* Edit document warning modal */}
      <Modal
        isOpen={showEditDocumentWarning}
        title="Edit document"
        onClose={onCloseEditDocumentWarningModal}
      >
        <Modal.Body>
          <p>
            Editing this copy will create a new revision, are you sure you want
            to edit?
          </p>
          {isErrorEditing && (
            <ErrorMessage message="Sorry, there was an issue editing your form. Please try again." />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={editDocument}
              isProcessing={isEditingDocument}
              replaceContentWhenProcessing
            >
              Proceed to edit
            </Button>
            <Button variant="link" onClick={onCloseEditDocumentWarningModal}>
              Cancel
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PreviewDocumentActionButtons;
